import { notification } from 'antd';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { apiPayCharge, apiPaymentStatus } from '../../../api/checkout';
import { apiSimulationTax } from '../../../api/simulation';
import { formatPrice } from '../../../utils/format';
import { searchPostalCode } from '../../../utils/searchPostalCode';
import { extractPhoneBackendErrors, validateCardNumber, validateCEP, validateEmail, validateName, validatePhoneForCountry, validateTaxpayerID } from '../../../utils/validatesCheckout';
import CountryCodeSelect, { DEFAULT_COUNTRY_CODE } from '../CountryCodeSelect/CountryCodeSelect';
import ExpiryDateSelector from '../ExpiryDateSelector/ExpiryDateSelector';
import InputCheckout from '../InputCheckout/InputCheckout';
import PhoneInputCheckout from '../PhoneInputCheckout/PhoneInputCheckout';
import StateSelect from '../StateSelect/StateSelect';
import ThreeDSChallenge from '../ThreeDSChallenge/ThreeDSChallenge';
import { buildThreeDsDevice, isThreeDsMessage } from '../../../utils/threeDS';
import './CheckoutForm.scss';
import { NodeExpandOutlined } from '@ant-design/icons';

const CheckoutForm = ({ infoProduct }) => {
  const [paymentType, setPaymentType] = useState('');
  const [isDisabled, setIsDisabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const hasPopulatedFromURL = useRef(false);
  const [formData, setFormData] = useState({
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    customerCountryCode: DEFAULT_COUNTRY_CODE,
    ccNumber: '',
    ccName: '',
    ccCvv: '',
    taxpayer: '',
    postalcode: '',
    addressCep: '',
    addressLine1: '',
    addressNumber: '',
    addressNeighborhood: '',
    addressCity: '',
    addressState: '',
    addressComplement: '',
  });

  const [errors, setErrors] = useState({});
  const [installments, setInstallments] = useState([]);
  const [selectedInstallment, setSelectedInstallment] = useState(null);
  const [returnPix, setReturnPix] = useState(false);
  const [returnBoleto, setReturnBoleto] = useState(false);
  const [returnCard, setReturnCard] = useState(false);
  const [errorPurchase, setErrorPurchase] = useState(false);
  const [errorDetails, setErrorDetails] = useState(null);
  const [threeDsChallenge, setThreeDsChallenge] = useState(null);
  const [threeDsVerifying, setThreeDsVerifying] = useState(false);
  const threeDsSettledRef = useRef(false);
  
  // Auto-checkout state management
  // eslint-disable-next-line no-unused-vars
  const [autoCheckoutTriggered, setAutoCheckoutTriggered] = useState(false);
  const autoCheckoutRef = useRef(false);
  const [autoCheckoutInProgress, setAutoCheckoutInProgress] = useState(false);

  const getInstallmentValue = (data, installment, buyerLiable) => {
    let installments = data[buyerLiable ? "buyer_liable" : "seller_liable"]["credit"]
    installment = installments[installment]
    let maxValue = {}
    for (let brand in installment) {
      let value = installment[brand]
      if (!parseFloat(maxValue.amount) || parseFloat(value.amount) > parseFloat(maxValue.amount)) {
        maxValue = value
      }
    }
    return maxValue
  }

  const fetchInstallments = useCallback(async (max_installments, max_installments_no_tax, sale_price, sale_seller_id) => {
    try {
      const data = await apiSimulationTax(sale_price.replace(",", "."), true, sale_seller_id);
      const newInstallments = [];

      for (let i = 0; i < parseInt(max_installments); i++) {
        const hasTax = (i + 1) > parseInt(max_installments_no_tax);
        const value = getInstallmentValue(data, i + 1, hasTax);
        const installmentFormatted = (parseFloat(value.amount) / (i + 1)).toFixed(2).replace(".", ",");

        newInstallments.push({
          text: `${i + 1}x de R$ ${installmentFormatted} ${hasTax ? "(com juros)" : "(sem juros)"}`,
          installments: i + 1
        });
      }

      setInstallments(newInstallments);
    } catch (error) {
      console.error('Erro ao buscar parcelas', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (infoProduct) {
      fetchInstallments(infoProduct.max_installments, infoProduct.max_installments_no_tax, infoProduct.sale_price, infoProduct.sale_seller_id);
    }
  }, [infoProduct, fetchInstallments]);

  useEffect(() => {
    if (installments.length > 0) {
      const firstInstallment = installments[0].installments;
      setSelectedInstallment(firstInstallment);
    }
  }, [installments, setSelectedInstallment]);

  const [selectedMonth, setSelectedMonth] = useState('01');
  const [selectedYear, setSelectedYear] = useState('24');
  const [validFields, setValidFields] = useState({});

  const validate = useCallback((overridePaymentType) => {
    const type = overridePaymentType || paymentType;
    const newErrors = {};
    // Validação de nome
    const nameError = validateName(formData?.customerName);
    if (nameError) {
      newErrors.customerName = nameError;
    }

    // Validação do e-mail
    const emailError = validateEmail(formData?.customerEmail);
    if (emailError) {
      newErrors.customerEmail = emailError;
    }

    // Validação do telefone
    const phoneError = validatePhoneForCountry(formData?.customerPhone, formData?.customerCountryCode);
    if (phoneError) {
      newErrors.customerPhone = phoneError;
    }

    if (type === 'card') {
      const ccNameError = validateName(formData?.ccName);
      if (ccNameError) {
        newErrors.ccName = ccNameError;
      }
      if (!formData.ccCvv) newErrors.ccCvv = 'Esse campo é obrigatório';

      const ccNumberError = validateCardNumber(formData?.ccNumber);
      if (ccNumberError) {
        newErrors.ccNumber = ccNumberError;
      }
      if (selectedInstallment === null) newErrors.installments = 'Esse campo é obrigatório';
    }

    if (type === 'boleto') {
      const taxpayerError = validateTaxpayerID(formData?.taxpayer);
      if (taxpayerError) {
        newErrors.taxpayer = taxpayerError;
      }

      const cepError = validateCEP(formData?.postalcode);
      if (cepError) {
        newErrors.postalcode = cepError;
      }

      if (!formData.addressCep) newErrors.addressCep = 'Esse campo é obrigatório';
      if (!formData.addressLine1) newErrors.addressLine1 = 'Esse campo é obrigatório';
      if (!formData.addressNumber) newErrors.addressNumber = 'Esse campo é obrigatório';
      if (!formData.addressNeighborhood) newErrors.addressNeighborhood = 'Esse campo é obrigatório';
      if (!formData.addressCity) newErrors.addressCity = 'Esse campo é obrigatório';
      if (!formData.addressState) newErrors.addressState = 'Esse campo é obrigatório';
    }

    if (type === 'pix') {
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [paymentType, formData, selectedInstallment]);

  const handlePhoneBackendError = useCallback((data) => {
    const { phone, countryCode } = extractPhoneBackendErrors(data);
    if (!phone && !countryCode) return false;

    if (countryCode) {
      console.warn('Backend rejected country_code:', countryCode);
    }

    setErrors((prev) => ({
      ...prev,
      ...(phone ? { customerPhone: phone } : {}),
      ...(countryCode ? { customerCountryCode: countryCode } : {}),
    }));
    setValidFields((prev) => ({
      ...prev,
      ...(phone ? { customerPhone: false } : {}),
      ...(countryCode ? { customerCountryCode: false } : {}),
    }));
    setIsDisabled(false);
    return true;
  }, []);

  const handleSubmit = useCallback((event, overridePaymentType) => {
    let type = overridePaymentType || paymentType;
    console.log('HandleSubmit called with paymentType:', type);
    if (event) {
      event.preventDefault();
    }
    if (!validate(type)) {
      return false;
    }
    setIsDisabled(true);
    if (type === 'card') {
        const payload = {
          email: formData.customerEmail,
          phone: formData.customerPhone,
          country_code: formData.customerCountryCode,
          name: formData.customerName,
          type: 'credit_card',
          installments: selectedInstallment,
          card: {
            number: formData.ccNumber.replace(/\s/g, ''),
            name: formData.ccName,
            cvv: formData.ccCvv,
            expirationMonth: selectedMonth,
            expirationYear: selectedYear
          }
        }
        if (infoProduct?.requires_3ds) {
          payload.three_ds = buildThreeDsDevice();
        }
        setLoading(true);
        apiPayCharge(infoProduct.sale_id, payload).then((data) => {
          setLoading(false)

          // Check for 402 credit card errors with detailed error information
          if (data?.httpStatus === 402 && data?.error) {
            setErrorDetails(data?.error);
            setErrorPurchase(true);
            return;
          }

          if (data?.httpStatus === 400 && handlePhoneBackendError(data)) {
            return;
          }

          const challenge = data?.payment?.three_ds;
          if (challenge?.required && challenge?.acs_url && challenge?.payment_id) {
            threeDsSettledRef.current = false;
            setThreeDsChallenge(challenge);
            return;
          }

          // Check for other payment errors
          if (data?.acquirerError || data?.payment?.status !== "succeeded") {
            setErrorDetails(null); // Clear specific error details for generic errors
            setErrorPurchase(true);
            return;
          }

          setReturnCard(data);
        }).catch(() => {
          setErrorDetails(null); // Clear specific error details for generic errors
          setErrorPurchase(true);
          setLoading(false);
        })
      }

      if (type === 'pix') {
        const payload = {
          email: formData.customerEmail,
          phone: formData.customerPhone,
          country_code: formData.customerCountryCode,
          name: formData.customerName,
          type: 'pix',
        }
        setLoading(true);
        apiPayCharge(infoProduct.sale_id, payload).then((data) => {
          setLoading(false);
          if (data?.httpStatus === 400 && handlePhoneBackendError(data)) {
            return;
          }
          if (data?.httpStatus >= 400) {
            setErrorDetails(null);
            setErrorPurchase(true);
            return;
          }
          setReturnPix(data)
        }).catch(() => {
          setErrorPurchase(true)
          setLoading(false)
        })
      }

      if (type === 'boleto') {
        const payload = {
          email: formData.customerEmail,
          phone: formData.customerPhone,
          country_code: formData.customerCountryCode,
          name: formData.customerName,
          type: 'boleto',
          taxpayerId: formData.taxpayer,
          address: {
            line1: formData.addressLine1,
            line2: formData.addressNumber,
            city: formData.addressCity,
            state: formData.addressState,
            postalCode: formData.addressCep.replace(/-/g, ''),
            neighborhood: formData.addressNeighborhood,
          }
        }
        setLoading(true);
        apiPayCharge(infoProduct.sale_id, payload).then((data) => {
          setLoading(false);
          if (data?.httpStatus === 400 && handlePhoneBackendError(data)) {
            return;
          }
          if (data?.httpStatus >= 400) {
            setErrorDetails(null);
            setErrorPurchase(true);
            return;
          }
          setReturnBoleto(data.payment)
        }).catch(() => {
          setErrorPurchase(true)
          setLoading(false)
        })
      }
  }, [paymentType, validate, selectedInstallment, selectedMonth, selectedYear, formData, infoProduct, handlePhoneBackendError]);

  const validateField = useCallback((name, value) => {
    let error = '';
    let isValid = true;

    // INITIAL FORMS VALIDATIONS
    if (name === 'customerName') {
      error = validateName(value);
      isValid = error === '';
    }

    if (name === 'customerPhone') {
      error = validatePhoneForCountry(value, formData.customerCountryCode);
      isValid = error === '';
    }

    if (name === 'customerEmail') {
      error = validateEmail(value);
      isValid = error === '';
    }
    // INITIAL FORMS VALIDATIONS

    // CARD VALIDATIONS
    if (name === 'ccName') {
      error = validateName(value);
      isValid = error === '';
    }

    if (name === 'ccNumber') {
      error = validateCardNumber(value);
      isValid = error === '';
    }

    if (name === 'taxpayer') {
      error = validateTaxpayerID(value);
      isValid = error === '';
    }

    //BOLETO VALIDATIONS
    if (name === 'postalcode') {
      error = validateCEP(value);
      if (error === '') {
        setPostalCodeData(null);
        checkPostalCode(value);
      }
      isValid = error === '';
    }

    if (value.trim() === '' && !error) {
      error = 'Este campo é obrigatório';
      isValid = false;
    }

    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: error,
    }));
    setValidFields((prevValid) => ({
      ...prevValid,
      [name]: isValid,
    }));
  }, [formData.customerCountryCode]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    const nextValue = name === 'ccNumber' ? value.replace(/\D/g, '') : value;

    setFormData((prevData) => ({
      ...prevData,
      [name]: nextValue,
    }));

    if (name === 'customerCountryCode') {
      setErrors((prev) => ({ ...prev, customerCountryCode: '' }));
      if (formData.customerPhone) {
        const error = validatePhoneForCountry(formData.customerPhone, value);
        const isValid = error === '';
        setErrors((prev) => ({ ...prev, customerPhone: error }));
        setValidFields((prev) => ({ ...prev, customerPhone: isValid }));
      }
      return;
    }

    validateField(name, nextValue);
  }, [validateField, formData.customerPhone]);

  useEffect(() => {
    if (hasPopulatedFromURL.current) return;

    const urlParams = new URLSearchParams(window.location.search);

    const nameParam = urlParams.get('name');
    const emailParam = urlParams.get('email');
    const phoneParam = urlParams.get('phone');

    // Boleto fields
    const taxpayerParam = urlParams.get('taxpayer');
    const cepParam = urlParams.get('cep');
    const addressParam = urlParams.get('address');
    const numberParam = urlParams.get('number');
    const complementParam = urlParams.get('complement');
    const neighborhoodParam = urlParams.get('neighborhood');
    const cityParam = urlParams.get('city');
    const stateParam = urlParams.get('state');

    const updates = {};

    if (nameParam) {
      updates.customerName = nameParam;
    }
    if (emailParam) {
      updates.customerEmail = emailParam;
    }
    if (phoneParam) {
      updates.customerPhone = phoneParam;
    }

    // Boleto field updates
    if (taxpayerParam) {
      updates.taxpayer = taxpayerParam;
    }
    if (cepParam) {
      updates.postalcode = cepParam;
      updates.addressCep = cepParam;
    }
    if (addressParam) {
      updates.addressLine1 = addressParam;
    }
    if (numberParam) {
      updates.addressNumber = numberParam;
    }
    if (complementParam) {
      updates.addressComplement = complementParam;
    }
    if (neighborhoodParam) {
      updates.addressNeighborhood = neighborhoodParam;
    }
    if (cityParam) {
      updates.addressCity = cityParam;
    }
    if (stateParam) {
      updates.addressState = stateParam;
    }

    if (Object.keys(updates).length > 0) {
      setFormData(prevData => ({
        ...prevData,
        ...updates
      }));

      setTimeout(() => {
        Object.entries(updates).forEach(([field, value]) => {
          validateField(field, value);
        });
      }, 0);

      hasPopulatedFromURL.current = true;
    }
  }, [validateField]);

  // Auto-checkout logic
  useEffect(() => {
    if (autoCheckoutRef.current) return;
    
    const urlParams = new URLSearchParams(window.location.search);
    const autoCheckoutType = urlParams.get('autoCheckout');
    
    if (!autoCheckoutType) return;
    
    // Check if all required data is loaded
    const isReady = 
      infoProduct && 
      !loading && 
      installments.length > 0 && 
      selectedInstallment !== null &&
      !isDisabled &&
      !errorPurchase &&
      !returnPix &&
      !returnBoleto &&
      !returnCard;
      
    if (!isReady) return;
    
    // Validate payment type
    const paymentTypeValid = 
      (autoCheckoutType === 'card' && infoProduct?.has_credit) ||
      (autoCheckoutType === 'boleto' && infoProduct?.has_boleto) ||
      (autoCheckoutType === 'pix' && infoProduct?.has_pix);
      
    if (!paymentTypeValid) {
      console.warn(`Invalid or unavailable payment type: ${autoCheckoutType}`);
      autoCheckoutRef.current = true; // Prevent retry
      return;
    }
    
    // Execute auto-checkout
    console.log('Initiating auto-checkout with type:', autoCheckoutType);
    autoCheckoutRef.current = true;
    setAutoCheckoutTriggered(true);
    setAutoCheckoutInProgress(true);
    
    // Set payment type
    setPaymentType(autoCheckoutType);
    
    // Trigger submit after brief delay for state updates
    setTimeout(() => {
      // For auto-checkout, we need minimal validation
      // Ensure required fields are present from URL params
      const requiredFieldsPresent =
        formData.customerName &&
        formData.customerEmail &&
        formData.customerPhone;

      if (requiredFieldsPresent || autoCheckoutType === 'pix') {
        console.log('Auto-checkout proceeding with submission');
        const submitted = handleSubmit(undefined, autoCheckoutType);
        if (submitted === false) {
          setAutoCheckoutInProgress(false);
        }
      } else {
        console.error('Auto-checkout failed: Missing required customer data');
        setAutoCheckoutInProgress(false);
      }
    }, 200);
    
  }, [
    infoProduct, 
    loading, 
    installments, 
    selectedInstallment, 
    isDisabled,
    errorPurchase,
    returnPix,
    returnBoleto,
    returnCard,
    formData,
    handleSubmit
  ]);

  const handleMonthChange = (e) => {
    setSelectedMonth(e.target.value);
  };

  const handleYearChange = (e) => {
    setSelectedYear(e.target.value);
  };

  // CEP VALIDATOR
  const [postalCodeData, setPostalCodeData] = useState(null);

  const checkPostalCode = (number) => {
    searchPostalCode(number, setPostalCodeData)
  }

  const setFieldValidity = (fieldName, isValid) => {
    setValidFields(prevValid => ({
      ...prevValid,
      [fieldName]: isValid
    }));
  };
  useEffect(() => {
    if (postalCodeData && !postalCodeData?.erro) {
      handleChange({ target: { name: 'addressLine1', value: postalCodeData?.logradouro } });
      handleChange({ target: { name: 'addressCity', value: postalCodeData?.localidade } });
      handleChange({ target: { name: 'addressNeighborhood', value: postalCodeData?.bairro } });
      handleChange({ target: { name: 'addressState', value: postalCodeData?.uf } });
      handleChange({ target: { name: 'addressCep', value: postalCodeData?.cep } });
      // Set fields as valid
      setFieldValidity('addressNeighborhood', true);
      setFieldValidity('addressState', true);
      setFieldValidity('addressCity', true);
      setFieldValidity('addressLine1', true);
    }
  }, [postalCodeData, handleChange]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(returnPix.payment.pix.copy_paste);
      notification.success({
        message: "Sucesso",
        description: "Código copiado com sucesso!",
        placement: "topRight",
      });
    } catch (err) {
      notification.error({
        message: "Erro",
        description: "Falha ao copiar o código.",
        placement: "topRight",
      });
    }
  };

  // ERROR PURCHASE
  function CircleAlertIcon(props) {
    return (
      <svg
        {...props}
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="12" cy="12" r="10" />
        <line x1="12" x2="12" y1="8" y2="12" />
        <line x1="12" x2="12.01" y1="16" y2="16" />
      </svg>
    );
  }

  function OrderUnsuccessful({ errorDetails }) {
    return (
      <div className="OrderUnsuccessful">
        <div className="content">
          <CircleAlertIcon className="icon" />
          <h1 className="title">Pedido malsucedido</h1>
            {errorDetails && (
              <div className="error-details">
                <p className="error-message">Erro {errorDetails.response_code}: {errorDetails.message_display}</p>
              </div>
            )}
            <p className="message">
              Lamentamos, mas houve um problema ao processar seu pagamento. Tente novamente ou entre em contato com o suporte ao cliente se o problema persistir.
            </p>
          <div className="actions">
            <button onClick={() => window.location.reload()} className="primary">Tentar novamente</button>
            <button className="secondary">Suporte</button>
          </div>
        </div>
      </div>
    );
  }

  const PaymentSuccess = () => {
    return (
      <div className="PaymentSuccess">
        <div className="content">
          <CircleCheckIcon className="icon" />
          <h1 className="title">Pagamento bem-sucedido!</h1>
          <p className="message">
            Obrigado pela sua compra. Seu pedido foi processado com sucesso.
          </p>
          <div className="order-info">
            <div className="total">{formatPrice(infoProduct?.sale_price)}</div>
          </div>
        </div>
      </div>
    );
  };

  const CircleCheckIcon = (props) => (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  );

  useEffect(() => {
    if (!threeDsChallenge?.payment_id || !infoProduct?.sale_id) return;

    let active = true;
    let attempts = 0;

    const settle = (status) => {
      if (!active || threeDsSettledRef.current) return;
      if (status === 'succeeded') {
        threeDsSettledRef.current = true;
        setThreeDsChallenge(null);
        setThreeDsVerifying(false);
        setReturnCard(true);
      } else if (status === 'failed' || status === 'canceled') {
        threeDsSettledRef.current = true;
        setThreeDsChallenge(null);
        setThreeDsVerifying(false);
        setErrorDetails(null);
        setErrorPurchase(true);
      }
    };

    const check = () => {
      apiPaymentStatus(infoProduct.sale_id, threeDsChallenge.payment_id)
        .then((data) => settle(data?.status))
        .catch(() => { });
    };

    const onMessage = (event) => {
      if (!isThreeDsMessage(event)) return;
      setThreeDsVerifying(true);
      check();
    };

    window.addEventListener('message', onMessage);
    const timer = setInterval(() => {
      attempts += 1;
      if (attempts > 100) {
        clearInterval(timer);
        return;
      }
      check();
    }, 3000);

    return () => {
      active = false;
      window.removeEventListener('message', onMessage);
      clearInterval(timer);
    };
  }, [threeDsChallenge, infoProduct]);

  return (
    <React.Fragment>
      {threeDsChallenge && !returnCard && !errorPurchase && (
        <div className="CheckoutForm">
          <ThreeDSChallenge challenge={threeDsChallenge} verifying={threeDsVerifying} />
        </div>
      )}
      {autoCheckoutInProgress && !returnPix && !returnBoleto && !returnCard && !errorPurchase && (
        <div className="auto-checkout-overlay">
          <div className="auto-checkout-message">
            <span className="loader"></span>
            <p>Processando checkout automático...</p>
          </div>
        </div>
      )}
      {
        !returnPix && !returnBoleto && !errorPurchase && !returnCard && !threeDsChallenge &&
        (<div className="CheckoutForm">
          <h2>Seus dados</h2>
          <form id="checkout-form" onSubmit={handleSubmit}>
            <div className="container-data">
              <div className="row">
                <InputCheckout
                  disabled={isDisabled}
                  label="Nome"
                  name="customerName"
                  value={formData.customerName}
                  onChange={handleChange}
                  errorMessage={errors.customerName}
                  isValid={validFields.customerName}
                />
              </div>
              <div className="row">
                <InputCheckout
                  disabled={isDisabled}
                  label="E-mail"
                  name="customerEmail"
                  type='email'
                  className="small"
                  value={formData.customerEmail}
                  onChange={handleChange}
                  errorMessage={errors.customerEmail}
                  isValid={validFields.customerEmail}
                />
                <div className="phone-row">
                  <CountryCodeSelect
                    disabled={isDisabled}
                    label="País"
                    name="customerCountryCode"
                    value={formData.customerCountryCode}
                    onChange={handleChange}
                    errorMessage={errors.customerCountryCode}
                  />
                  <PhoneInputCheckout
                    disabled={isDisabled}
                    label="Telefone"
                    name="customerPhone"
                    className="small"
                    value={formData.customerPhone}
                    onChange={handleChange}
                    errorMessage={errors.customerPhone}
                    isValid={validFields.customerPhone}
                  />
                </div>
              </div>
            </div>
            <div className="payment-type-container">
              <h2>Forma de Pagamento</h2>
              <div className="payment_buttons">
                {infoProduct?.has_credit && <button type="button" onClick={() => setPaymentType('card')} className={`payment_type_button ${paymentType === 'card' ? 'active' : ''}`}>Cartão de Crédito</button>}
                {infoProduct?.has_boleto && <button type="button" onClick={() => setPaymentType('boleto')} className={`payment_type_button ${paymentType === 'boleto' ? 'active' : ''}`}>Boleto</button>}
                {infoProduct?.has_pix && <button type="button" onClick={() => setPaymentType('pix')} className={`payment_type_button ${paymentType === 'pix' ? 'active' : ''}`}>PIX</button>}
              </div>
            </div>
            {paymentType === 'card' && <div className="payment_form">
              <InputCheckout
                disabled={isDisabled}
                label="Número do cartão"
                name="ccNumber"
                // className='small'
                value={formData.ccNumber}
                onChange={handleChange}
                errorMessage={errors.ccNumber}
                isValid={validFields.ccNumber} />
              {/* <InputCheckout
               disabled={isDisabled}
                label="Número do cartão"
                name="ccNumber"
                value={ccNumber}
                onChange={handleChangeCC}
                onBlur={handleBlurCC}
                errorMessage={errorCC}
                isValid={isValidCC}
              /> */}

              <div className="row">
                <ExpiryDateSelector
                  selectedMonth={selectedMonth}
                  selectedYear={selectedYear}
                  onMonthChange={handleMonthChange}
                  onYearChange={handleYearChange}
                />
                <InputCheckout
                  disabled={isDisabled}
                  label="CVV"
                  name="ccCvv"
                  className='small'
                  value={formData.ccCvv}
                  maxLength={4}
                  onChange={handleChange}
                  errorMessage={errors.ccCvv}
                  isValid={validFields.ccCvv} />
              </div>

              <InputCheckout
                disabled={isDisabled}
                label="Nome do Portador"
                name="ccName"
                value={formData.ccName}
                onChange={handleChange}
                errorMessage={errors.ccName}
                isValid={validFields.ccName}
              />
              <div className="installments-container">
                <label htmlFor="installments-select">Parcelas</label>
                <select
                  id="installments-select"
                  disabled={loading}
                  onChange={(e) => {
                    setSelectedInstallment(e.target.value)
                  }}
                >
                  {installments.map((installment) => (
                    <option key={installment.installments} value={installment.installments}>
                      {installment.text}
                    </option>
                  ))}
                </select>
              </div>

            </div>}

            {/* BOLETO */}
            {paymentType === 'boleto' && <div className="payment_form">
              <div className="payment__redirect">
                O Banco solicita algumas informações extras para a geração do boleto.
              </div>
              <InputCheckout
                disabled={isDisabled}
                label="CPF ou CNPJ"
                name="taxpayer"
                value={formData.taxpayer}
                onChange={handleChange}
                errorMessage={errors.taxpayer}
                isValid={validFields.taxpayer}
              />
              <InputCheckout
                disabled={isDisabled}
                label="CEP"
                name="postalcode"
                value={formData.postalcode}
                onChange={handleChange}
                errorMessage={errors.postalcode}
                isValid={validFields.postalcode}
                maxLength={9}
              />

              <InputCheckout
                disabled={isDisabled}
                label="Endereço"
                name="addressLine1"
                value={formData.addressLine1}
                onChange={handleChange}
                errorMessage={errors.addressLine1}
                isValid={validFields.addressLine1}
              />

              <div className="row">
                <InputCheckout
                  disabled={isDisabled}
                  label="Número"
                  name="addressNumber"
                  className="small"
                  value={formData.addressNumber}
                  onChange={handleChange}
                  errorMessage={errors.addressNumber}
                  isValid={validFields.addressNumber}
                />
                <InputCheckout
                  disabled={isDisabled}
                  label="Complemento"
                  name="addressComplement"
                  className="small"
                  value={formData.addressComplement}
                  onChange={handleChange}
                  isValid={formData.addressComplement ? true : ''}
                />
              </div>

              <InputCheckout
                disabled={isDisabled}
                label="Bairro"
                name="addressNeighborhood"
                value={formData.addressNeighborhood}
                onChange={handleChange}
                errorMessage={errors.addressNeighborhood}
                isValid={validFields.addressNeighborhood}
              />

              <div className="row">
                <InputCheckout
                  disabled={isDisabled}
                  label="Cidade"
                  name="addressCity"
                  className="small"
                  value={formData.addressCity}
                  onChange={handleChange}
                  errorMessage={errors.addressCity}
                  isValid={validFields.addressCity}
                />

                <StateSelect
                  label="Estado"
                  name="addressState"
                  value={formData.addressState}
                  onChange={handleChange}
                  errorMessage={errors.addressState}
                  isValid={validFields.addressState}
                />
              </div>

            </div>}

            {paymentType === 'pix' && <div className="payment_form">
              <div className="payment__redirect">
                Ao clicar em "Pagar" você será redirecionado para o QRCode do PIX.
              </div>
            </div>}

            <div className="payment-container">
              {loading ?
                <div className="loading-div">
                  <span className="loader-text">Processando...</span>
                  <span className="loader"></span>
                </div>
                : <button onClick={(e) => handleSubmit(e)}>PAGAR</button>}
            </div>
          </form>
        </div>)
      }

      {/* // PIX */}
      {returnPix &&
        <React.Fragment>
          <div className='pix-feedback'>
            <h2>Seu QRCode está pronto!</h2>
            <img src={returnPix.payment.pix.qr_code} alt="QR Code" width={250} />
            <h3>Pix Copia e Cola</h3>
            <div className='pix-copy-paste'>
              <div className='pix-code'>{returnPix.payment.pix.copy_paste}</div>
              <button onClick={handleCopy}>Copiar</button>
            </div>
          </div>
        </React.Fragment>
      }

      {/* // BOLETO */}
      {returnBoleto &&
        <React.Fragment>
          <div className='boleto-feedback'>
            <h2>Visualizar Boleto</h2>
            <div>
              <button onClick={() => window.open(returnBoleto?.boleto?.url, '_blank')}>Visualizar página</button>
            </div>
            <iframe
              src={returnBoleto?.boleto?.url}
              width="100%"
              height="600px"
              title="Boleto"
              style={{ border: 'none', marginTop: '24px' }}
            />
          </div>
        </React.Fragment>
      }
      {returnCard && <PaymentSuccess />}
      {errorPurchase && <OrderUnsuccessful errorDetails={errorDetails} />}
    </React.Fragment>

  );
};

export default CheckoutForm;
